import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { generatedTypesNew, BillingStatus } from '@css-xp/graphtypes';
import { TransactionAccount, EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { STATUSPAGE_PATH_2, NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import capitalize from 'lodash/capitalize';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import VerticalMoreIcon from '@atlaskit/icon/glyph/more-vertical';
import { transformFilterableFields } from '@atlassian/cssxp-utils-generic';
import { useTransactionAccountContext } from '../../../common-context';
import { FormattedDate, FormattedAddress } from '../../../formatted';
import { DynamicResultTable, HeadCell } from '../../../dynamic-result-table';
import { fetchSubscriptionsForTransactionAccount } from './queries/fetch-subscriptions-for-transaction-account';
import { CustomSearch } from '../../../custom-search';
import { CustomFilterWrapper } from '../../../custom-filter';
import { CustomizeView } from '../../../customize-view';
import { CloudProductDetailView, PaymentMethodView } from '../../../entitlement-utils';
import { NoDataContentInTable } from '../../../no-data-content-table';
import { getBilledForColumnData, isActiveSubscription } from '../../../entitlement-utils/src/utils';
import './subscription.scss';
import { NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import { NewBillDate } from '../../../new-bill-date';

export interface SubscriptionTabProps {
  transactionAccountId?: string;
  label?: string;
  showFilters?: boolean;
}

interface FormData {
  searchTerm: string;
}

const DEFAULT_PAGE_SIZE = 8;

const FILTER_FIELDS = [
  {
    filterItem: 'Status',
    queryKey: 'billingStatus',
    iterableValues: transformFilterableFields<typeof BillingStatus>(BillingStatus),
  },
  // TODO: Product and Payment Method need to be added
];

export const SubscriptionTabTransactionAccount = ({
  transactionAccountId,
  label,
  showFilters = false,
}: SubscriptionTabProps) => {
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterOptions, setFilterOptions] = useState<{ key: string; value: string }[]>([]);
  const [searchOptions, setSearchOptions] = useState<{ key: string; value: string }[]>([]);

  const { data, loading, error, refetch } = useQuery<
    generatedTypesNew.FetchSubscriptionsForTransactionAccountQuery,
    generatedTypesNew.FetchSubscriptionsForTransactionAccountQueryVariables
  >(fetchSubscriptionsForTransactionAccount, {
    variables: {
      id: transactionAccountId ?? '',
      searchOptions: { first: rowsPerPage },
    },
    skip: !transactionAccountId,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  });

  const subscriptions = useMemo(() => data?.transactionAccount?.productSubscriptions?.nodes, [data]);
  const totalCount = useMemo(() => data?.transactionAccount?.productSubscriptions?.totalCount ?? 0, [data]);
  const headData = useMemo(() => {
    const cells = [
      {
        content: 'Subscription',
        width: 25,
      },
      {
        content: 'Site',
        width: 20,
        isMandatory: false,
        isChecked: true,
      },
      {
        content: 'Billed for',
        width: 15,
        isMandatory: false,
        isChecked: true,
      },
      {
        content: 'Next bill/ Expiry',
        width: 20,
      },
      {
        content: 'Billing cycle',
        width: 15,
        isMandatory: false,
        isChecked: true,
      },
      {
        content: 'Payment method',
        width: 20,
        isMandatory: false,
        isChecked: true,
      },
      {
        content: 'Sold-to address',
        width: 20,
        isMandatory: false,
        isChecked: true,
      },
    ];

    return {
      cells,
    };
  }, []);

  const { data: transactionAccountData, getTransactionAccount } = useTransactionAccountContext();
  const transactionAccount = getTransactionAccount(transactionAccountData);

  const history = useHistory();

  const [columnsList, setColumnList] = useState<{ cells: Array<HeadCell> }>(headData);

  const getRowAction = useCallback(
    (product: generatedTypesNew.TransactionAccountProduct) => {
      return (
        <DropdownMenu
          position="bottom right"
          triggerType="button"
          triggerButtonProps={{
            appearance: 'subtle',
            className: 'trigger-button',
            iconBefore: <VerticalMoreIcon label="invoice-action" />,
          }}
          testId={`actions-${product?.id}`}
        >
          <DropdownItemGroup>
            <DropdownItem
              key={'add-promotion'}
              onClick={() => {
                history.push(`${STATUSPAGE_PATH_2}/${encodeURIComponent(product?.id)}`);
              }}
            >
              View details
            </DropdownItem>
          </DropdownItemGroup>
        </DropdownMenu>
      );
    },
    [history]
  );

  const getSoldToAddress = useCallback(
    (invoiceGroupId: string | undefined | null) => {
      let currentInvoiceGroupDetails;
      if (!invoiceGroupId) {
        currentInvoiceGroupDetails = transactionAccount?.availableInvoiceGroups?.nodes[0];
      } else {
        currentInvoiceGroupDetails = transactionAccount?.availableInvoiceGroups?.nodes?.find(
          (invoiceGroupObj) => invoiceGroupObj?.id === invoiceGroupId
        );
      }
      if (currentInvoiceGroupDetails) {
        return (
          <div className="line-clamp">
            <FormattedAddress
              addressLine1={currentInvoiceGroupDetails?.shipToParty?.addressLine1}
              addressLine2={currentInvoiceGroupDetails?.shipToParty?.addressLine2}
              city={currentInvoiceGroupDetails?.shipToParty?.city}
              country={currentInvoiceGroupDetails?.shipToParty?.country}
              postcode={currentInvoiceGroupDetails?.shipToParty?.postcode}
              state={currentInvoiceGroupDetails?.shipToParty?.state}
            />
          </div>
        );
      }
      return 'No address information';
    },
    [transactionAccount]
  );

  const onPageChange = useCallback(
    (pageNumber: number) => {
      setCurrentPage(pageNumber);
      refetch({
        searchOptions: {
          offset: (pageNumber - 1) * rowsPerPage,
          first: rowsPerPage,
        },
        filterOptions: searchOptions.concat(filterOptions),
      });
    },
    [rowsPerPage, refetch, searchOptions, filterOptions]
  );

  const onSearch = useCallback(
    async (formData: FormData) => {
      const currentSearchOptions = [];
      const searchInput = formData?.searchTerm;
      if (searchInput) {
        currentSearchOptions.push({
          key: 'entitlementNumber',
          value: searchInput,
        });
      }
      setSearchOptions(currentSearchOptions);
      setCurrentPage(1);
      refetch({
        filterOptions: filterOptions.concat(currentSearchOptions),
        searchOptions: {
          first: DEFAULT_PAGE_SIZE,
          offset: 0,
        },
      });
    },
    [refetch, filterOptions]
  );

  const onFilter = useCallback(
    (selectedFilters: { key: string; value: string }[]) => {
      setFilterOptions(selectedFilters);
      setCurrentPage(1);
      refetch({
        filterOptions: searchOptions.concat(selectedFilters),
        searchOptions: {
          first: DEFAULT_PAGE_SIZE,
          offset: 0,
        },
      });
    },
    [refetch, searchOptions]
  );

  const getColumnData = useCallback(
    (header: string, product: generatedTypesNew.TransactionAccountProduct) => {
      switch (header) {
        case 'Subscription':
          return {
            content: (
              <CloudProductDetailView
                productId={product?.productId as string}
                entitlementNumber={product?.entitlementNumber || ''}
                trial={product?.trial ?? false}
                billingStatus={product?.billingStatus}
                provisioningStatus={product?.provisioningStatus}
                edition={product?.edition}
                productName={product?.productName}
                isSandbox={product?.isSandbox ?? false}
                upgradeTrial={product?.upgradeTrial}
              />
            ),
          };
        case 'Site': {
          const siteName = product?.cloudSiteHostname;
          return {
            content: siteName ? (
              <div className="line-clamp site-name-container" title={siteName}>
                {siteName}
              </div>
            ) : (
              <NoDataContentInTable />
            ),
          };
        }
        case 'Billed for':
          return {
            content: (
              <div>
                {product?.billedUsers && (
                  <div>{capitalize(getBilledForColumnData(product?.productId, product?.edition))}</div>
                )}
                <div>{product?.billedUsers ?? '-'}</div>
              </div>
            ),
          };
        case 'Next bill/ Expiry':
          return {
            content:
              product?.latestPeriodEndDate &&
              isActiveSubscription(product?.billingStatus, product?.provisioningStatus) ? (
                <div>
                  <FormattedDate date={product?.latestPeriodEndDate} format={NEW_DATE_FORMAT} />
                  <NewBillDate entitlement={product as EntitlementDetails} />
                </div>
              ) : (
                <NoDataContentInTable />
              ),
          };
        case 'Billing cycle':
          return {
            content: product?.billingFrequency ? capitalize(product.billingFrequency) : <NoDataContentInTable />,
          };
        case 'Sold-to address':
          return {
            content: getSoldToAddress(product?.billingGroupId),
          };
        case 'Payment method':
          return {
            content: (
              <PaymentMethodView
                invoiceGroupId={product?.billingGroupId}
                transactionAccount={transactionAccount as TransactionAccount}
              />
            ),
          };
        default:
          return {
            content: 'NA',
          };
      }
    },
    [getSoldToAddress, transactionAccount]
  );

  const handleRowsChange = useCallback((rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(1);
  }, []);

  const selectedColumns = useMemo(() => {
    const columns = columnsList.cells.filter(
      (column) => column.isChecked || !Object.prototype.hasOwnProperty.call(column, 'isChecked')
    );
    return { cells: columns };
  }, [columnsList]);

  const rowData = useMemo(() => {
    return (subscriptions ?? []).map(
      //@ts-ignore
      (product: generatedTypesNew.TransactionAccountProduct) => {
        return {
          cells: [
            ...selectedColumns.cells.map((columnData) => getColumnData(columnData.content as string, product)),
            {
              content: getRowAction(product),
            },
          ],
        };
      }
    );
  }, [subscriptions, getColumnData, selectedColumns, getRowAction]);

  return (
    <DynamicResultTable
      headingText={label ?? 'Subscriptions'}
      head={{
        cells: [
          ...selectedColumns.cells,
          {
            content: '',
            width: 2,
          },
        ],
      }}
      rows={rowData}
      emptyStateProps={{
        header: '',
        description: NO_DETAILS_TO_SHOW,
      }}
      isLoading={loading}
      totalCount={totalCount}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      isPaginationEnabled={rowData && rowData.length > 0 && totalCount > DEFAULT_PAGE_SIZE}
      onRowChange={handleRowsChange}
      showFilters={showFilters}
      customiseColumnView={
        <CustomizeView
          columns={columnsList.cells}
          updatedColumnView={(record: Array<HeadCell>) => setColumnList({ cells: [...record] })}
        />
      }
    >
      <div className="subscription-table-filter">
        <CustomSearch placeholder={'Search all subscriptions'} onSearch={onSearch} isLoading={loading} />
        <CustomFilterWrapper filterFields={FILTER_FIELDS} handleFilterUpdate={onFilter} />
      </div>
    </DynamicResultTable>
  );
};
